import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { type Provider, type Account, AccountState } from '@repo/api-gw-sdk';
import React from 'react';

import { useRoles } from '@/contexts/useRoles';
import { CloudProviders } from '@/data/inventory/data';

import { SelectPlaceholder } from '../selectPlaceholder';
import { EmptySelect } from '../selectors/emptySelect';
import { TextLink } from '../shared/textLink';

export type VaultManagedType = 'eon-managed' | `self-managed-${Provider}`;

export const VaultManagedTypeSelect = ({
  title,
  selfManagedVaultAccounts,
  showEonManaged,
  restrictedCloudProviders,
  region,
  regionRequired,
  vaultManagedType,
  onChange,
}: {
  title: string;
  selfManagedVaultAccounts: Account[];
  showEonManaged: boolean;
  restrictedCloudProviders: Provider[];
  region?: string;
  regionRequired: boolean;
  vaultManagedType?: VaultManagedType;
  onChange: (vaultManagedType: VaultManagedType) => void;
}) => {
  const { isAuthorized } = useRoles();
  if (!selfManagedVaultAccounts.length || (regionRequired && !region)) {
    return <EmptySelect title={title} size='medium' />;
  }

  const onSelect = (event: SelectChangeEvent<VaultManagedType>) => {
    onChange(event.target.value as VaultManagedType);
  };

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Select
        sx={{ paddingLeft: '8px' }}
        size='medium'
        value={vaultManagedType || ''}
        onChange={onSelect}
        displayEmpty
        renderValue={(value: string) => {
          if (!value) {
            return <SelectPlaceholder />;
          }
          return getLabel(value as VaultManagedType);
        }}
      >
        {showEonManaged && (
          <MenuItem key='eon-managed' value='eon-managed'>
            Eon-managed (recommended)
          </MenuItem>
        )}
        {Object.values(restrictedCloudProviders).map((cloudProvider) => {
          const disabled =
            (regionRequired && !region) ||
            selfManagedVaultAccounts.filter(
              (a) =>
                a.cloudProvider === cloudProvider &&
                a.state !== AccountState.ACCOUNT_STATE_DISCONNECTED
            ).length === 0;

          const menuItem = (
            <MenuItem
              disabled={disabled}
              key={`self-managed-${cloudProvider}`}
              value={`self-managed-${cloudProvider}`}
            >
              {getLabel(`self-managed-${cloudProvider}`)}
            </MenuItem>
          );

          const path = `/global-settings/vault-accounts/${cloudProvider.toLowerCase() === 'azure' ? 'azure-subscriptions' : cloudProvider.toLowerCase() === 'gcp' ? 'gcp-projects' : 'aws-accounts'}`;

          return disabled ? (
            <Tooltip
              disableInteractive={false}
              key={`self-managed-${cloudProvider}`}
              title={
                <Typography className='text-inherit'>
                  No vault accounts are connected. To create a vault, connect at
                  least one vault account.{' '}
                  <TextLink
                    className='text-inherit hover:text-inherit'
                    disabled={!isAuthorized('read:vault_accounts')}
                    href={path}
                  >
                    Manage vault accounts.
                  </TextLink>
                </Typography>
              }
            >
              <span>{menuItem}</span>
            </Tooltip>
          ) : (
            menuItem
          );
        })}
      </Select>
    </FormControl>
  );
};

const getLabel = (vaultType: VaultManagedType) => {
  if (vaultType === 'eon-managed') {
    return 'Eon-managed (recommended)';
  }
  return `Self-managed in ${CloudProviders[vaultType.split('self-managed-')[1] as Provider].displayName}`;
};
